import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import ApiService from "../../services/api";
import {
  Search,
  Download,
  Eye,
  Mail,
  X,
  Calendar,
  FileText,
  User,
  CreditCard,
  DollarSign,
  MapPin,
  Phone,
  Building2,
  Package,
} from "lucide-react";

interface Partner {
  city: string;
  name: string;
  email: string;
  state: string;
  country: string;
  address_1: string;
  address_2: string;
  postal_code: string;
  phone_number: string;
}

interface Shopper {
  city: string;
  name: string;
  type: string;
  email: string;
  state: string;
  country: string;
  address_1: string;
  postal_code: string;
  phone_number: string;
}

interface Invoice {
  id: number;
  invoice_number: number;
  order_id: string;
  partner: Partner;
  shopper: Shopper;
  invoice_date: string;
  due_date: string;
  amount: number;
  status: string;
  payment_method: string;
}

interface Service {
  invoice_id: number;
  date: string;
  service: string;
  description: string;
  quantity: number;
  unit_price: number;
}

interface InvoiceDetails extends Invoice {
  services: Service[];
}

const AdminInvoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState("");
  const [orderIdFilter, setOrderIdFilter] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 10;

  // Modal and action states
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetails | null>(
    null
  );
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState<number | null>(
    null
  );
  const [resendingInvoice, setResendingInvoice] = useState<number | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, [
    currentPage,
    searchTerm,
    invoiceNumberFilter,
    orderIdFilter,
    filterStatus,
  ]);

  const fetchInvoices = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: any = {
        page: currentPage,
        pageSize: pageSize,
      };

      if (searchTerm.trim()) {
        params.search_text = searchTerm.trim();
      }

      if (invoiceNumberFilter.trim()) {
        params.invoice_number = invoiceNumberFilter.trim();
      }

      if (orderIdFilter.trim()) {
        params.order_id = orderIdFilter.trim();
      }

      if (filterStatus !== "all") {
        params.payment_status = filterStatus;
      }

      const response = await ApiService.getInvoices(params);

      if (response.success) {
        setInvoices(response.data);
        setTotalPages(response.totalPages);
        setTotalItems(response.total);
      } else {
        throw new Error(response.message || "Failed to fetch invoices");
      }
    } catch (err: any) {
      console.error("Error fetching invoices:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch invoices"
      );
      setInvoices([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = async (invoiceNumber: number) => {
    setLoadingDetails(true);
    setError(null);
    setShowDetailsModal(true);

    try {
      const response = await ApiService.getDetailsByInvoiceNumber(
        invoiceNumber
      );

      if (response.success) {
        setSelectedInvoice(response.data);
      } else {
        throw new Error(response.message || "Failed to fetch invoice details");
      }
    } catch (err: any) {
      console.error("Error fetching invoice details:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch invoice details"
      );
      setShowDetailsModal(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleDownloadInvoice = async (invoiceNumber: number) => {
    setDownloadingInvoice(invoiceNumber);
    setError(null);

    try {
      const blob = await ApiService.getInvoiceReceipt(invoiceNumber);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccessMessage("Invoice downloaded successfully");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error("Error downloading invoice:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to download invoice"
      );
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleResendInvoice = async (invoiceNumber: number) => {
    setResendingInvoice(invoiceNumber);
    setError(null);

    try {
      const response = await ApiService.resendInvoiceOnEmail(invoiceNumber);

      if (response.success) {
        setSuccessMessage(response.message || "Invoice resent successfully");
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(response.message || "Failed to resend invoice");
      }
    } catch (err: any) {
      console.error("Error resending invoice:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to resend invoice"
      );
    } finally {
      setResendingInvoice(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case "CAPTURED":
      case "PAID":
      case "SETTLED":
      case "COMPLETED":
      case "SUCCESS":
      case "APPROVED":
        return "bg-green-100 text-green-800";
      case "PENDING":
      case "AUTHORISED":
      case "AUTHORIZED":
        return "bg-yellow-100 text-yellow-800";
      case "FAILED":
      case "REFUSED":
      case "DECLINED":
      case "REJECTED":
      case "ERROR":
        return "bg-red-100 text-red-800";
      case "CANCELLED":
      case "REFUNDED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (type: string, value: any) => {
    setCurrentPage(1);
    switch (type) {
      case "status":
        setFilterStatus(value);
        break;
      case "invoiceNumber":
        setInvoiceNumberFilter(value);
        break;
      case "orderId":
        setOrderIdFilter(value);
        break;
    }
  };

  // Invoice Card Component for Mobile
  const InvoiceCard = ({ invoice }: { invoice: Invoice }) => (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4 w-full overflow-hidden hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900">
              Invoice #{invoice.invoice_number}
            </h3>
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getStatusColor(
                invoice.status
              )}`}
            >
              {invoice.status}
            </span>
          </div>
          <div className="space-y-1">
            <div className="text-sm text-gray-600 flex items-center min-w-0">
              <FileText className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">
                Gateway Order ID: {invoice.order_id}
              </span>
            </div>
            <div className="text-sm text-gray-600 flex items-center min-w-0">
              <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">
                {formatDate(invoice.invoice_date)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Info */}
      <div className="border-t border-gray-200 pt-3">
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
          Customer
        </h4>
        <div className="flex items-start space-x-3">
          <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <User className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">
              {invoice.shopper.name}
            </div>
            <div className="text-sm text-gray-600 flex items-center min-w-0">
              <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">{invoice.shopper.email}</span>
            </div>
            {invoice.shopper.phone_number && (
              <div className="text-sm text-gray-600 flex items-center min-w-0">
                <Phone className="h-3 w-3 mr-1 flex-shrink-0" />
                <span className="truncate">{invoice.shopper.phone_number}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Info */}
      <div className="border-t border-gray-200 pt-3">
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
          Payment
        </h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-gray-900 flex items-center">
              <DollarSign className="h-3 w-3 mr-1" />
              Total Amount
            </div>
            <div className="text-sm font-semibold text-gray-900">
              {formatCurrency(invoice.amount)}
            </div>
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <CreditCard className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <span className="text-sm text-gray-600 truncate">
              {invoice.payment_method}
            </span>
          </div>
          <div className="text-sm text-gray-600 flex items-center">
            <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
            Due: {formatDate(invoice.due_date)}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="border-t border-gray-200 pt-3 flex flex-wrap gap-2">
        <button
          onClick={() => handleViewDetails(invoice.invoice_number)}
          className="flex-1 min-w-[100px] inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
        >
          <Eye className="h-4 w-4 mr-1" />
          View
        </button>
        <button
          onClick={() => handleDownloadInvoice(invoice.invoice_number)}
          disabled={downloadingInvoice === invoice.invoice_number}
          className="flex-1 min-w-[100px] inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
        >
          {downloadingInvoice === invoice.invoice_number ? (
            <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></div>
          ) : (
            <>
              <Download className="h-4 w-4 mr-1" />
              Download
            </>
          )}
        </button>
        <button
          onClick={() => handleResendInvoice(invoice.invoice_number)}
          disabled={resendingInvoice === invoice.invoice_number}
          className="flex-1 min-w-[100px] inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-50"
        >
          {resendingInvoice === invoice.invoice_number ? (
            <div className="animate-spin h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full"></div>
          ) : (
            <>
              <Mail className="h-4 w-4 mr-1" />
              Resend
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Invoice Details Modal
  const InvoiceDetailsModal = () => {
    if (!showDetailsModal) return null;

    return (
      <div className="fixed inset-0 bg-black/7 backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {loadingDetails ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : selectedInvoice ? (
            <>
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Invoice #{selectedInvoice.invoice_number}
                  </h2>
                  <p className="text-sm text-gray-600">
                    Gateway Order ID: {selectedInvoice.order_id}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Status and Dates */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                      Status
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        selectedInvoice.status
                      )}`}
                    >
                      {selectedInvoice.status}
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                      Invoice Date
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {formatDate(selectedInvoice.invoice_date)}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                      Due Date
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {formatDate(selectedInvoice.due_date)}
                    </div>
                  </div>
                </div>

                {/* Customer Details */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Customer Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Name</div>
                      <div className="text-sm font-medium text-gray-900">
                        {selectedInvoice.shopper.name}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Email</div>
                      <div className="text-sm text-gray-900">
                        {selectedInvoice.shopper.email}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Phone</div>
                      <div className="text-sm text-gray-900">
                        {selectedInvoice.shopper.phone_number}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Type</div>
                      <div className="text-sm text-gray-900 capitalize">
                        {selectedInvoice.shopper.type}
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-xs text-gray-500 mb-1">Address</div>
                      <div className="text-sm text-gray-900">
                        {selectedInvoice.shopper.address_1}
                        <br />
                        {selectedInvoice.shopper.city},{" "}
                        {selectedInvoice.shopper.state}{" "}
                        {selectedInvoice.shopper.postal_code}
                        <br />
                        {selectedInvoice.shopper.country}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Services */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <Package className="h-4 w-4 mr-2" />
                    Services
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Date
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Service
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Description
                          </th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                            Qty
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            Unit Price
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedInvoice.services.map((service, index) => (
                          <tr key={index}>
                            <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">
                              {formatDate(service.date)}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900">
                              {service.service}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-600">
                              {service.description}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900 text-center">
                              {service.quantity}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900 text-right whitespace-nowrap">
                              {formatCurrency(service.unit_price)}
                            </td>
                            <td className="px-3 py-2 text-sm font-medium text-gray-900 text-right whitespace-nowrap">
                              {formatCurrency(
                                service.quantity * service.unit_price
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">
                      Payment Method:
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedInvoice.payment_method}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                    <span className="text-base font-semibold text-gray-900">
                      Total Amount:
                    </span>
                    <span className="text-lg font-bold text-gray-900">
                      {formatCurrency(selectedInvoice.amount)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() =>
                      handleDownloadInvoice(selectedInvoice.invoice_number)
                    }
                    disabled={
                      downloadingInvoice === selectedInvoice.invoice_number
                    }
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {downloadingInvoice === selectedInvoice.invoice_number ? (
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </>
                    )}
                  </button>
                  <button
                    onClick={() =>
                      handleResendInvoice(selectedInvoice.invoice_number)
                    }
                    disabled={
                      resendingInvoice === selectedInvoice.invoice_number
                    }
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  >
                    {resendingInvoice === selectedInvoice.invoice_number ? (
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Resend Email
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <AdminLayout title="Invoices">
      <div className="space-y-4 sm:space-y-6 w-full min-w-0">
        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex justify-between items-start">
              <div className="text-sm text-green-700 break-words">
                {successMessage}
              </div>
              <button
                onClick={() => setSuccessMessage(null)}
                className="ml-4 text-green-700 hover:text-green-900 flex-shrink-0"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex justify-between items-start">
              <div className="text-sm text-red-700 break-words">{error}</div>
              <button
                onClick={() => setError(null)}
                className="ml-4 text-red-700 hover:text-red-900 flex-shrink-0"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              Invoices
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Total {totalItems}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                />
              </div>
            </div>

            {/* Invoice Number */}
            <div>
              <input
                type="text"
                placeholder="Invoice Number..."
                value={invoiceNumberFilter}
                onChange={(e) =>
                  handleFilterChange("invoiceNumber", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>

            {/* Order ID */}
            <div>
              <input
                type="text"
                placeholder="Order ID..."
                value={orderIdFilter}
                onChange={(e) => handleFilterChange("orderId", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="CAPTURED">CAPTURED</option>
                <option value="AUTHORISED">AUTHORISED</option>
                <option value="PENDING">PENDING</option>
                <option value="REFUSED">REFUSED</option>
                <option value="DECLINED">DECLINED</option>
                <option value="FAILED">FAILED</option>
                <option value="ERROR">ERROR</option>
              </select>
            </div>
          </div>
        </div>

        {/* Invoices Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 w-full overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dates
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoices.map((invoice) => (
                      <tr
                        key={invoice.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              Invoice #{invoice.invoice_number}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <FileText className="h-3 w-3 mr-1 flex-shrink-0" />
                              <span className="truncate">
                                Gateway Order ID: {invoice.order_id}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                              <User className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {invoice.shopper.name}
                              </div>
                              <div className="text-sm text-gray-500 truncate">
                                {invoice.shopper.email}
                              </div>
                              {invoice.shopper.phone_number && (
                                <div className="text-sm text-gray-500 truncate">
                                  {invoice.shopper.phone_number}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 flex items-center">
                              <DollarSign className="h-3 w-3 mr-1 flex-shrink-0" />
                              <span className="truncate">
                                {formatCurrency(invoice.amount)}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <CreditCard className="h-3 w-3 mr-1 flex-shrink-0" />
                              <span className="truncate">
                                {invoice.payment_method}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="min-w-0">
                            <div className="text-sm text-gray-900">
                              <Calendar className="h-3 w-3 mr-1 inline flex-shrink-0" />
                              {formatDate(invoice.invoice_date)}
                            </div>
                            <div className="text-sm text-gray-500">
                              Due: {formatDate(invoice.due_date)}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                              invoice.status
                            )}`}
                          >
                            {invoice.status}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() =>
                                handleViewDetails(invoice.invoice_number)
                              }
                              className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-colors"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleDownloadInvoice(invoice.invoice_number)
                              }
                              disabled={
                                downloadingInvoice === invoice.invoice_number
                              }
                              className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                              title="Download PDF"
                            >
                              {downloadingInvoice === invoice.invoice_number ? (
                                <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></div>
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() =>
                                handleResendInvoice(invoice.invoice_number)
                              }
                              disabled={
                                resendingInvoice === invoice.invoice_number
                              }
                              className="text-purple-600 hover:text-purple-900 p-1 hover:bg-purple-50 rounded transition-colors disabled:opacity-50"
                              title="Resend Email"
                            >
                              {resendingInvoice === invoice.invoice_number ? (
                                <div className="animate-spin h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                              ) : (
                                <Mail className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile/Tablet Card View */}
              <div className="lg:hidden p-4 space-y-4 w-full overflow-hidden">
                {invoices.map((invoice) => (
                  <InvoiceCard key={invoice.id} invoice={invoice} />
                ))}
              </div>

              {invoices.length === 0 && !isLoading && (
                <div className="text-center py-12 px-4">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No invoices found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchTerm ||
                    invoiceNumberFilter ||
                    orderIdFilter ||
                    filterStatus !== "all"
                      ? "Try adjusting your search or filter criteria."
                      : "No invoices have been created yet."}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Invoice Details Modal */}
      <InvoiceDetailsModal />
    </AdminLayout>
  );
};

export default AdminInvoices;
